import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import {
  TERM_SHEET_DISCLAIMER,
  TERM_SHEET_FONT_FAMILY,
  TERM_SHEET_PREVIEW_SX,
  TERM_SHEET_PREVIEW_TEXT_SX,
} from "./termSheetConstants";
import {
  formatTermSheetLabel,
  getVisibleTermSheetSections,
} from "./termSheetHelpers";
import type { TermSheetSectionRow } from "./types";

interface TermSheetPreviewProps {
  title: string;
  date: string;
  sections: TermSheetSectionRow[];
  closingDisclaimer: string;
}

export function TermSheetPreview({
  title,
  date,
  sections,
  closingDisclaimer,
}: TermSheetPreviewProps) {
  const visibleSections = getVisibleTermSheetSections(sections);

  return (
    <Paper
      elevation={0}
      sx={{
        ...TERM_SHEET_PREVIEW_SX,
        p: 3,
        borderRadius: 0,
        boxShadow: "none",
        fontFamily: TERM_SHEET_FONT_FAMILY,
        "& .MuiTableCell-root": {
          fontFamily: TERM_SHEET_FONT_FAMILY,
          fontSize: "11pt",
          lineHeight: 1.35,
          color: "#000000",
        },
        "& .MuiTypography-root": {
          fontFamily: TERM_SHEET_FONT_FAMILY,
        },
      }}
    >
      <Typography
        variant="inherit"
        align="center"
        sx={{
          ...TERM_SHEET_PREVIEW_TEXT_SX,
          fontWeight: 700,
          mb: 1,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="inherit"
        align="center"
        sx={{
          ...TERM_SHEET_PREVIEW_TEXT_SX,
          mb: 2,
        }}
      >
        {date}
      </Typography>

      <Typography
        variant="inherit"
        sx={{
          ...TERM_SHEET_PREVIEW_TEXT_SX,
          textAlign: "justify",
          mb: 2,
        }}
      >
        <Box component="span" sx={{ fontWeight: 700 }}>
          DISCLAIMER:
        </Box>{" "}
        {TERM_SHEET_DISCLAIMER}
      </Typography>

      <TableContainer>
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            tableLayout: "fixed",
            width: "100%",
            "& td": {
              border: "1px solid #000000",
              verticalAlign: "top",
              px: 1.25,
              py: 0.75,
            },
          }}
        >
          <TableBody>
            {visibleSections.map((section) => (
              <TableRow key={section.id}>
                <TableCell sx={{ width: "25%" }}>
                  <Typography
                    variant="inherit"
                    component="div"
                    sx={{
                      ...TERM_SHEET_PREVIEW_TEXT_SX,
                      fontWeight: 700,
                    }}
                  >
                    {formatTermSheetLabel(section.label)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: "75%" }}>
                  <Typography
                    variant="inherit"
                    component="div"
                    sx={{
                      ...TERM_SHEET_PREVIEW_TEXT_SX,
                      textAlign: "justify",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {section.content}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {closingDisclaimer.trim() ? (
        <Box sx={{ mt: 2 }}>
          {closingDisclaimer.startsWith("DISCLAIMER") ? (
            <>
              <Typography
                variant="inherit"
                sx={{
                  ...TERM_SHEET_PREVIEW_TEXT_SX,
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                DISCLAIMER
              </Typography>
              <Typography
                variant="inherit"
                sx={{
                  ...TERM_SHEET_PREVIEW_TEXT_SX,
                  textAlign: "justify",
                  whiteSpace: "pre-wrap",
                }}
              >
                {closingDisclaimer.replace(/^DISCLAIMER\s*\n+/, "")}
              </Typography>
            </>
          ) : (
            <Typography
              variant="inherit"
              sx={{
                ...TERM_SHEET_PREVIEW_TEXT_SX,
                textAlign: "justify",
                whiteSpace: "pre-wrap",
              }}
            >
              {closingDisclaimer}
            </Typography>
          )}
        </Box>
      ) : null}
    </Paper>
  );
}
